import { graphql } from 'gatsby';

import { contentfulProps } from 'assets/mockData/emptyTypes';

import Section from 'molecules/Section/Section';

import AuthorHero from 'components/Author/AuthorHero';
import AuthorPosts from 'components/Author/AuthorPosts';

import Layout from 'layouts/Layout';
import LayoutSection from 'layouts/LayoutSection';
import SEO from 'layouts/SEO';

import type { HeadFC, PageProps } from 'gatsby';
import type {
  ContentfulEntityPerson,
  ContentfulLayoutSection,
  ContentfulMetaSeo,
  ContentfulTemplateBlogPostConnection,
  ContentfulTemplateCompanyNewsConnection,
  ContentfulTemplateVideoConnection,
} from 'graphqlTypes';
import type { FC } from 'react';

interface AuthorTemplateData {
  authorData: ContentfulEntityPerson;
  blogPosts: ContentfulTemplateBlogPostConnection;
  companyNewsPosts: ContentfulTemplateCompanyNewsConnection;
  videoPosts: ContentfulTemplateVideoConnection;
  conversionPanelSection: ContentfulLayoutSection;
}

const Author: FC<PageProps<AuthorTemplateData>> = ({
  data: { authorData, blogPosts, companyNewsPosts, videoPosts, conversionPanelSection },
}) => {
  const posts = [...blogPosts.nodes, ...companyNewsPosts.nodes, ...videoPosts.nodes].sort((a, b) => {
    const dateA = new Date(a.publishDate).getTime();
    const dateB = new Date(b.publishDate).getTime();

    return dateB - dateA;
  });

  return (
    <Layout>
      <Section
        id="author-hero"
        paddingTopDesktop={56}
        paddingTopTablet={48}
        paddingTopMobile={32}
        paddingBottomDesktop={56}
        paddingBottomTablet={48}
        paddingBottomMobile={32}
      >
        <AuthorHero {...authorData} />
      </Section>
      <Section
        id="author-posts"
        paddingTopDesktop={56}
        paddingTopTablet={48}
        paddingTopMobile={32}
        paddingBottomDesktop={56}
        paddingBottomTablet={48}
        paddingBottomMobile={32}
      >
        <AuthorPosts posts={posts} />
      </Section>
      <LayoutSection {...conversionPanelSection} />
    </Layout>
  );
};

export default Author;

export const Head: HeadFC<AuthorTemplateData> = ({
  data: {
    authorData: { fullName },
  },
  location: { pathname },
}) => {
  const seo: ContentfulMetaSeo = {
    ...contentfulProps,
    pageTitle: fullName,
    pageDescription: `Blog posts, Company News, and Videos from author ${fullName}.`,
    indexable: true,
  };

  return <SEO pathname={pathname} {...seo} />;
};

export const authorPageQuery = graphql`
  query author($id: String!) {
    authorData: contentfulEntityPerson(id: { eq: $id }) {
      ...contentfulEntityPerson
    }
    blogPosts: allContentfulTemplateBlogPost(filter: { author: { id: { eq: $id } } }, sort: { publishDate: DESC }) {
      nodes {
        ...contentfulTemplateBlogPost
      }
    }
    companyNewsPosts: allContentfulTemplateCompanyNews(
      filter: { author: { id: { eq: $id } } }
      sort: { publishDate: DESC }
    ) {
      nodes {
        ...contentfulTemplateCompanyNews
      }
    }
    videoPosts: allContentfulTemplateVideo(filter: { author: { id: { eq: $id } } }, sort: { publishDate: DESC }) {
      nodes {
        ...contentfulTemplateVideo
      }
    }
    conversionPanelSection: contentfulLayoutSection(contentful_id: { eq: "64HI5xQBb7P3N3vDzTg4S0" }) {
      ...contentfulLayoutSection
    }
  }
`;
